import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../../ui/button';
import { Switch } from '../../ui/switch';
import { TestBadge } from '../../tax/TaxBadges';
import { usePayApi, money, when } from './shared';

const copy = (t) => navigator.clipboard.writeText(t).then(() => toast.success('Link copied'));

export const PaymentsLinks = ({ token }) => {
  const { data } = usePayApi(token, '/links');
  if (!data) return <p className="text-sm text-neutral-500 p-6">Loading…</p>;
  return (
    <div className="space-y-4" data-testid="payments-links">
      <div className="bg-white border rounded-lg p-4">
        <p className="font-medium text-sm mb-2">Invoice Pay Now links ({data.invoices.length})</p>
        <table className="w-full text-xs"><tbody>{data.invoices.map((i) => (
          <tr key={i.id} className="border-b" data-testid={`link-invoice-${i.invoice_number}`}>
            <td className="py-2 pr-3 font-mono">{i.invoice_number} <TestBadge doc={i} testId={`link-test-${i.invoice_number}`} /></td><td className="pr-3">{i.customer_name}</td><td className="pr-3">{money(i.balance_due)} due</td><td className="pr-3 capitalize">{i.status}</td>
            <td className="text-right"><Button size="sm" variant="outline" className="h-7" onClick={() => copy(i.link)}>Copy link</Button></td></tr>
        ))}</tbody></table>
      </div>
      <div className="bg-white border rounded-lg p-4">
        <p className="font-medium text-sm mb-2">Quote deposits ({data.deposits.length})</p>
        <table className="w-full text-xs"><thead><tr className="text-left text-neutral-500 border-b">{['Quote', 'Customer', 'Quote total', 'Deposit required', 'Deposit paid', 'Remaining balance', ''].map((h) => <th key={h} className="py-2 pr-3 font-medium">{h}</th>)}</tr></thead>
          <tbody>{data.deposits.map((d) => (
            <tr key={d.quote_id} className="border-b" data-testid={`link-deposit-${d.quote_id}`}>
              <td className="py-2 pr-3 font-mono">#{d.quote_id.slice(0, 8).toUpperCase()} <TestBadge doc={d} testId={`link-dep-test-${d.quote_id}`} /></td><td className="pr-3">{d.customer_name}</td>
              <td className="pr-3">{money(d.quote_total)}</td><td className="pr-3">{money(d.deposit_required)}{d.type === 'percent' ? ` (${d.value}%)` : ''}</td><td className="pr-3">{money(d.deposit_paid)}</td><td className="pr-3">{money(d.remaining_balance)}</td>
              <td className="text-right"><Button size="sm" variant="outline" className="h-7" onClick={() => copy(d.link)}>Copy portal link</Button></td></tr>
          ))}</tbody></table>
        <p className="text-[11px] text-neutral-400 mt-2">Customers pay deposits after signing in to the secure customer portal.</p>
      </div>
    </div>
  );
};

export const PaymentsWebhooks = ({ token }) => {
  const { data } = usePayApi(token, '/webhooks');
  if (!data) return <p className="text-sm text-neutral-500 p-6">Loading…</p>;
  const st = data.status.stripe || {};
  return (
    <div className="bg-white border rounded-lg p-4 space-y-3" data-testid="payments-webhooks">
      <div className="grid sm:grid-cols-4 gap-3 text-sm">
        <div><p className="text-xs text-neutral-500">Last verified event</p><p data-testid="webhook-last-verified">{when(st.last_verified_at)}</p></div>
        <div><p className="text-xs text-neutral-500">Rejected deliveries</p><p data-testid="webhook-rejected-count">{st.rejected_count || 0}</p></div>
        <div className="sm:col-span-2"><p className="text-xs text-neutral-500">Last rejection</p><p>{st.last_rejected_at ? `${when(st.last_rejected_at)} · ${st.last_rejected_reason}` : '—'}</p></div>
      </div>
      <p className="text-xs text-neutral-500">Events are accepted only with a valid signature from the matching mode. Each event is processed once; repeats are recorded as duplicates and ignored.</p>
      <table className="w-full text-xs"><thead><tr className="text-left text-neutral-500 border-b">{['Received', 'Provider', 'Event', 'Mode', 'Session', 'Result'].map((h) => <th key={h} className="py-2 pr-3 font-medium">{h}</th>)}</tr></thead>
        <tbody>{data.events.map((e) => (
          <tr key={`${e.provider}-${e.event_id}`} className="border-b" data-testid={`webhook-row-${e.event_id}`}>
            <td className="py-1.5 pr-3">{when(e.received_at)}</td><td className="pr-3 capitalize">{e.provider}</td><td className="pr-3 font-mono">{e.type}</td><td className="pr-3">{e.livemode ? 'Live' : 'Test'}</td>
            <td className="pr-3 font-mono max-w-[12rem] truncate">{e.session_id || '—'}</td><td className="pr-3">{e.result}</td></tr>
        ))}</tbody></table>
    </div>
  );
};

const FEATURES = [['checkout', 'Online checkout (website cart)'], ['invoice', 'Invoice payments'], ['payment_links', 'Payment links (emailed Pay Now links)'], ['deposit', 'Quote deposits'], ['refunds', 'Refunds from Admin']];
const ROUTES = [['checkout', 'Default online checkout provider'], ['invoice', 'Invoice payment provider'], ['payment_link', 'Payment-link provider'], ['deposit', 'Deposit provider']];

export const PaymentsSettings = ({ token }) => {
  const { data, send } = usePayApi(token, '/overview');
  const [form, setForm] = useState(null);
  useEffect(() => { if (data) setForm({ routing: { ...data.routing }, features: { ...data.features } }); }, [data]);
  if (!data || !form) return <p className="text-sm text-neutral-500 p-6">Loading…</p>;
  const ed = data.can_edit;
  return (
    <div className="bg-white border rounded-lg p-5 space-y-5 max-w-2xl" data-testid="payments-settings">
      <div><p className="font-medium text-sm mb-2">Payment types</p>
        {FEATURES.map(([k, l]) => (
          <label key={k} className="flex items-center justify-between py-2 border-b text-sm">{l}
            <Switch checked={!!form.features[k]} disabled={!ed} onCheckedChange={(v) => setForm({ ...form, features: { ...form.features, [k]: v } })} data-testid={`pay-setting-${k}`} /></label>
        ))}
        <p className="text-[11px] text-neutral-500 mt-2">When a payment type is off, customers see: “{data.unavailable_message}”</p></div>
      <div><p className="font-medium text-sm mb-2">Which provider handles what</p>
        {ROUTES.map(([k, l]) => (
          <label key={k} className="flex items-center justify-between py-2 border-b text-sm">{l}
            <select value={form.routing[k]} disabled={!ed} onChange={(e) => setForm({ ...form, routing: { ...form.routing, [k]: e.target.value } })} className="h-8 border rounded-md px-2" data-testid={`pay-route-${k}`}>
              {data.providers.map((p) => <option key={p.name} value={p.name}>{p.label}</option>)}
            </select></label>
        ))}
        <p className="text-[11px] text-neutral-500 mt-2">Switching provider affects new payments only. A failed charge is never retried automatically on a different processor.</p></div>
      {ed ? <Button onClick={() => send('put', '/settings', form, 'Payment settings saved')} data-testid="pay-settings-save">Save settings</Button> : <p className="text-xs text-amber-700">View only: Super Admins and Admins change payment settings.</p>}
    </div>
  );
};

export const PaymentsAudit = ({ token }) => {
  const { data } = usePayApi(token, '/audit');
  return (
    <div className="bg-white border rounded-lg p-4" data-testid="payments-audit">
      <table className="w-full text-xs"><thead><tr className="text-left text-neutral-500 border-b">{['When', 'Who', 'Action', 'Details'].map((h) => <th key={h} className="py-2 pr-3 font-medium">{h}</th>)}</tr></thead>
        <tbody>{(data || []).map((a) => (
          <tr key={a.id} className="border-b align-top" data-testid={`audit-row-${a.id}`}><td className="py-1.5 pr-3 whitespace-nowrap">{when(a.at)}</td><td className="pr-3">{a.by}</td><td className="pr-3 font-medium">{a.action.replace(/_/g, ' ')}</td>
            <td className="pr-3 font-mono text-[11px] break-all">{JSON.stringify(a.details)}</td></tr>
        ))}</tbody></table>
      {data && !data.length && <p className="text-sm text-neutral-500 p-4">No payment changes recorded yet.</p>}
    </div>
  );
};
